import type {Page} from '@PosterWhiteboard/page/page.class';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {TBBox} from '@postermywall/fabricjs-2';
import {CANVAS_BACKDROP_CLASS, INNER_PAGE_CONTAINER_CLASS, PAGE_CONTAINER_CLASS} from '@PosterWhiteboard/page/page.types';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import {POSTER_ELEMENT_CONTAINER} from '@Components/poster-whiteboard/poster-whiteboard.types';
import type {MarqueeDragSession, MarqueePoint} from './marquee-selection-layer.types';

export function getWhiteboardContainer(): HTMLElement | undefined {
  return window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get?.(0);
}

export function clientPointToScenePoint(clientPoint: MarqueePoint, backdropRect: DOMRect, zoom: number): MarqueePoint {
  return {
    x: (clientPoint.x - backdropRect.left) / zoom,
    y: (clientPoint.y - backdropRect.top) / zoom,
  };
}

export function getSceneRectFromCorners(cornerA: MarqueePoint, cornerB: MarqueePoint): TBBox {
  return {
    left: Math.min(cornerA.x, cornerB.x),
    top: Math.min(cornerA.y, cornerB.y),
    width: Math.abs(cornerA.x - cornerB.x),
    height: Math.abs(cornerA.y - cornerB.y),
  };
}

/**
 * A marquee press in the gray area must land on the whiteboard's own structural surfaces, never
 * on chrome layered over them (page sliders, controls, popups, overlays) — an allowlist keeps
 * those out without having to enumerate them.
 */
export function isStructuralWhiteboardTarget(target: Element): boolean {
  if (target === getWhiteboardContainer() || target === window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD]?.get?.(0)) {
    return true;
  }
  return target.classList.contains(POSTER_ELEMENT_CONTAINER) || target.classList.contains(PAGE_CONTAINER_CLASS) || target.classList.contains(INNER_PAGE_CONTAINER_CLASS);
}

export function getNearestVisiblePageToClientPoint(poster: Poster, clientPoint: MarqueePoint): Page | undefined {
  let nearestPage: Page | undefined;
  let nearestDistanceSquared = Number.POSITIVE_INFINITY;
  for (const pageId of poster.pages.pageOrder) {
    const page = poster.pages.getPage(pageId);
    if (!page) {
      continue;
    }
    const backdropRect = page.canvasBackdrop.getBoundingClientRect();
    if (backdropRect.width === 0 || backdropRect.height === 0) {
      continue;
    }
    const dx = Math.max(backdropRect.left - clientPoint.x, 0, clientPoint.x - backdropRect.right);
    const dy = Math.max(backdropRect.top - clientPoint.y, 0, clientPoint.y - backdropRect.bottom);
    const distanceSquared = dx * dx + dy * dy;
    if (distanceSquared < nearestDistanceSquared) {
      nearestDistanceSquared = distanceSquared;
      nearestPage = page;
    }
  }
  return nearestPage;
}

/**
 * A press is eligible to become a marquee from (a) empty canvas or the background — fabric's own
 * hit test already ran on the bubbling mousedown, so the page's marquee-start state is the oracle —
 * or (b) the gray whiteboard area, resolved to the nearest visible page. A press on a regular item
 * returns null so the item drag/select proceeds untouched.
 */
export function createMarqueeSessionForMouseDown(poster: Poster, e: MouseEvent): MarqueeDragSession | null {
  // Crop and refine-cutout modes own the drag on every surface: a press on the gray whiteboard resolves
  // to the nearest page without consulting that page's marquee-start state, so it has to be turned away here too.
  if (poster.isOnCanvasEditModeActive()) {
    return null;
  }
  if (!(e.target instanceof Element)) {
    return null;
  }
  const clientPoint: MarqueePoint = {x: e.clientX, y: e.clientY};
  const backdropElement = e.target.closest(`.${CANVAS_BACKDROP_CLASS}`);
  let startPage: Page | undefined;
  let startedOnCanvas = false;

  if (backdropElement) {
    const page = poster.pages.getPage(backdropElement.id);
    if (!page?.hasPotentialMarqueeStart()) {
      return null;
    }
    startPage = page;
    startedOnCanvas = true;
  } else {
    if (!isStructuralWhiteboardTarget(e.target)) {
      return null;
    }
    startPage = getNearestVisiblePageToClientPoint(poster, clientPoint);
  }
  if (!startPage) {
    return null;
  }

  return {
    startPage,
    anchorScene: clientPointToScenePoint(clientPoint, startPage.canvasBackdrop.getBoundingClientRect(), poster.scaling.scale),
    startClient: clientPoint,
    lastClient: clientPoint,
    didPassThreshold: false,
    startedOnCanvas,
    backgroundFabricObject: startedOnCanvas ? (startPage.items.getActiveBackground()?.fabricObject ?? null) : null,
  };
}

export function setMarqueeDragSelectInProgressOnAllPages(inProgress: boolean): void {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }
  for (const pageId of poster.pages.pageOrder) {
    poster.pages.getPage(pageId)?.setMarqueeDragSelectInProgress(inProgress);
  }
}

export function applyMarqueeSelection(session: MarqueeDragSession, e: MouseEvent): void {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }
  const backdropRect = session.startPage.canvasBackdrop.getBoundingClientRect();
  const releaseScene = clientPointToScenePoint({x: e.clientX, y: e.clientY}, backdropRect, poster.scaling.scale);
  session.startPage.activeSelection.selectItemsIntersectingSceneRect(getSceneRectFromCorners(session.anchorScene, releaseScene), e);
}

/**
 * With fabric's group selector disabled, its isClick flag makes a click that drifted a couple of
 * pixels on the background select nothing on mouseup. Restore the old behavior: a sub-threshold
 * release from a canvas press that left nothing selected re-selects the background. Shift-presses
 * stay untouched — fabric's multi-selection handling owns those.
 */
export function reselectBackgroundAfterDriftClick(session: MarqueeDragSession, e: MouseEvent): void {
  if (!session.startedOnCanvas || !session.backgroundFabricObject || e.shiftKey) {
    return;
  }
  const selectionManager = session.startPage.activeSelection;
  if (selectionManager.getActiveObject()) {
    return;
  }
  selectionManager.setActiveObject(session.backgroundFabricObject, e);
  session.startPage.fabricCanvas.requestRenderAll();
}
